import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { trackPracticeView, trackPhoneClick, trackEmailClick, trackWebsiteClick, trackSocialClick } from '../utils/analytics';
import { formatPhoneNumber, formatPhoneForCall } from '../utils/formatters';
import { APP_NAME } from '../config/branding';

// Simple SVG icons to avoid dependency issues
const MapPinIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const PhoneIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

const EnvelopeIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const GlobeAltIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
  </svg>
);

const ClockIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

interface Practice {
  uuid: string;
  name: string;
  description?: string;
  phone?: string;
  email?: string;
  website?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  slug?: string;
  specialties: string[];
  attributes?: {
    parking?: boolean;
    wheelchair_accessible?: boolean;
    emergency_services?: boolean;
    [key: string]: any;
  };
  social_links?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
    [key: string]: string | undefined;
  };
  timezone: string;
  is_active: boolean;
  accepts_new_patients: boolean;
  hours_of_operation?: string;
  hours?: Array<{
    day: string;
    open: string;
    close: string;
    closed: boolean;
  }>;
}

const PracticeShow: React.FC = () => {
  const { state, city, slug } = useParams<{ state: string; city: string; slug: string }>();
  const navigate = useNavigate();
  const [practice, setPractice] = useState<Practice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoursExpanded, setHoursExpanded] = useState(false);

  useEffect(() => {
    const fetchPractice = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/practices/practice/${state}/${city}/${slug}`
        );

        if (!response.ok) {
          if (response.status === 404) {
            setError('Practice not found');
          } else {
            setError('Failed to load practice information');
          }
          return;
        }

        const data = await response.json();
        setPractice(data);
        
        // Track practice page view
        trackPracticeView(data.name, data.slug || slug || 'unknown');
        
        // Set canonical URL for SEO
        const canonicalUrl = `https://helppet.ai/practice/${state}/${city}/${slug}`;
        let canonicalLink = document.querySelector('link[rel="canonical"]');
        if (canonicalLink) {
          canonicalLink.setAttribute('href', canonicalUrl);
        } else {
          canonicalLink = document.createElement('link');
          canonicalLink.setAttribute('rel', 'canonical');
          canonicalLink.setAttribute('href', canonicalUrl);
          document.head.appendChild(canonicalLink);
        }
        
        // Set page title
        document.title = `${data.name} - ${data.city}, ${data.state} | ${APP_NAME}`;
      } catch (err) {
        console.error('Error fetching practice:', err);
        setError('Failed to load practice information');
      } finally {
        setLoading(false);
      }
    };

    if (state && city && slug) {
      fetchPractice();
    }
  }, [state, city, slug]);

  // Generate Google Static Maps URL
  const getStaticMapUrl = () => {
    if (!practice?.address) return null;
    
    const address = encodeURIComponent(practice.address);
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
    
    // If no API key, return null
    if (!apiKey) return null;
    
    return `https://maps.googleapis.com/maps/api/staticmap?center=${address}&zoom=15&size=600x400&markers=color:red%7C${address}&key=${apiKey}`;
  };

  const formatAttributeName = (key: string): string => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatTime = (time: string): string => {
    // Convert 24h time (e.g., "09:00") to 12h format (e.g., "9:00 AM")
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getCurrentDayName = (): string => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[new Date().getDay()];
  };

  const isOpenNow = (hours?: Array<{day: string; open: string; close: string; closed: boolean}>): boolean => {
    if (!hours || hours.length === 0) return false;
    
    const now = new Date();
    const currentDay = getCurrentDayName();
    const currentTime = now.getHours() * 60 + now.getMinutes(); // minutes since midnight
    
    const todayHours = hours.find(h => h.day === currentDay);
    if (!todayHours || todayHours.closed) return false;
    
    const [openHour, openMin] = todayHours.open.split(':').map(Number);
    const [closeHour, closeMin] = todayHours.close.split(':').map(Number);
    const openTime = openHour * 60 + openMin;
    const closeTime = closeHour * 60 + closeMin;
    
    return currentTime >= openTime && currentTime < closeTime;
  };

  const getNextOpeningTime = (hours?: Array<{day: string; open: string; close: string; closed: boolean}>): string => {
    if (!hours || hours.length === 0) return 'tomorrow';
    
    const now = new Date();
    const currentDay = getCurrentDayName();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    
    // Check if open later today
    const todayHours = hours.find(h => h.day === currentDay);
    if (todayHours && !todayHours.closed) {
      const [openHour, openMin] = todayHours.open.split(':').map(Number);
      const openTime = openHour * 60 + openMin;
      
      if (currentTime < openTime) {
        return formatTime(todayHours.open);
      }
    }
    
    // Find next open day
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const currentDayIndex = days.indexOf(currentDay);
    
    for (let i = 1; i <= 7; i++) {
      const nextDayIndex = (currentDayIndex + i) % 7;
      const nextDay = days[nextDayIndex];
      const nextDayHours = hours.find(h => h.day === nextDay);
      
      if (nextDayHours && !nextDayHours.closed) {
        const dayLabel = i === 1 ? 'tomorrow' : nextDay.substring(0, 3);
        return `${formatTime(nextDayHours.open)} ${dayLabel !== 'tomorrow' ? dayLabel : ''}`.trim();
      }
    }
    
    return 'tomorrow';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading practice information...</p>
        </div>
      </div>
    );
  }

  if (error || !practice) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Practice Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'The practice you are looking for does not exist.'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  const mapUrl = getStaticMapUrl();

  return (
    <div className="min-h-screen bg-gray-50 py-4">
        {/* Breadcrumb Schema for SEO */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://helppet.ai"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Practices",
                "item": "https://helppet.ai/practices"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": state,
                "item": `https://helppet.ai/practice/${state}`
              },
              {
                "@type": "ListItem",
                "position": 4,
                "name": `${city}, ${state}`,
                "item": `https://helppet.ai/practice/${state}/${city}`
              },
              {
                "@type": "ListItem",
                "position": 5,
                "name": practice?.name || "Practice",
                "item": `https://helppet.ai/practice/${state}/${city}/${slug}`
              }
            ]
          })}
        </script>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
          <div className="p-8">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">{practice.name}</h1>
                
                {practice.description && (
                  <p className="text-lg text-gray-600 mb-6">{practice.description}</p>
                )}

                <div className="flex flex-wrap gap-2 mb-4">
                  {practice.specialties && practice.specialties.length > 0 && practice.specialties.map((specialty, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                    >
                      {specialty}
                    </span>
                  ))}
                  
                  {practice.accepts_new_patients && (
                    <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                      ✓ Accepting New Patients
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Main Content */}
          <div className="space-y-8">
            {/* Map Section */}
            {mapUrl && (
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <img
                  src={mapUrl}
                  alt={`Map showing location of ${practice.name}`}
                  className="w-full h-64 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(practice.address || practice.name)}`, '_blank')}
                />
              </div>
            )}

            {/* Attributes Section */}
            {practice.attributes && Object.keys(practice.attributes).length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Amenities & Features</h2>
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(practice.attributes).map(([key, value]) => (
                    value && (
                      <div key={key} className="flex items-center">
                        <span className="text-green-600 mr-2">✓</span>
                        <span className="text-gray-700">{formatAttributeName(key)}</span>
                      </div>
                    )
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Information */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h3>
                
                <div className="space-y-3">
                {practice.address && (
                  <div className="text-sm">
                    <span className="font-semibold text-gray-900">Address: </span>
                    <span className="text-gray-600">{practice.address}</span>
                  </div>
                )}

                {practice.phone && (
                  <div className="text-sm">
                    <span className="font-semibold text-gray-900">Phone: </span>
                    <a 
                      href={`tel:${formatPhoneForCall(practice.phone)}`} 
                      className="text-blue-600 hover:underline"
                      onClick={() => trackPhoneClick(practice.name, practice.phone!)}
                    >
                      {formatPhoneNumber(practice.phone)}
                    </a>
                  </div>
                )}

                {practice.email && (
                  <div className="text-sm">
                    <span className="font-semibold text-gray-900">Email: </span>
                    <a 
                      href={`mailto:${practice.email}`} 
                      className="text-blue-600 hover:underline break-all"
                      onClick={() => trackEmailClick(practice.name, practice.email!)}
                    >
                      {practice.email}
                    </a>
                  </div>
                )}

                {practice.website && (
                  <div className="text-sm">
                    <span className="font-semibold text-gray-900">Website: </span>
                    <a 
                      href={practice.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline break-all"
                      onClick={() => trackWebsiteClick(practice.name, practice.website!)}
                    >
                      {practice.website}
                    </a>
                  </div>
                )}

                {practice.hours && practice.hours.length > 0 && (
                  <div className="text-sm">
                    <div 
                      className="flex items-center justify-between cursor-pointer hover:bg-gray-50 -mx-2 px-2 py-1 rounded"
                      onClick={() => setHoursExpanded(!hoursExpanded)}
                    >
                      <div>
                        <span className="font-semibold text-gray-900">Hours: </span>
                        <span className={`font-semibold ${isOpenNow(practice.hours) ? 'text-green-600' : 'text-red-600'}`}>
                          {isOpenNow(practice.hours) ? 'Open' : 'Closed'}
                        </span>
                        <span className="text-gray-600"> · Opens {getNextOpeningTime(practice.hours)}</span>
                      </div>
                      <svg 
                        className={`w-5 h-5 text-gray-600 transition-transform ${hoursExpanded ? 'rotate-180' : ''}`} 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                    {hoursExpanded && (
                      <div className="mt-2 space-y-0.5 pl-0">
                        {practice.hours.map((dayHours, index) => {
                          const isToday = dayHours.day === getCurrentDayName();
                          return (
                            <div 
                              key={index} 
                              className={`flex justify-between text-sm ${isToday ? 'font-bold text-gray-900' : 'text-gray-600'}`}
                            >
                              <span>{dayHours.day.substring(0, 3)}</span>
                              <span>{dayHours.closed ? 'Closed' : `${formatTime(dayHours.open)} - ${formatTime(dayHours.close)}`}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="mt-4">
                <a
                  href="/signup"
                  className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Own this practice?
                </a>
              </div>
            </div>

            {/* Social Links */}
            {practice.social_links && Object.keys(practice.social_links).length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Follow Us</h3>
                <div className="space-y-3">
                  {Object.entries(practice.social_links).map(([platform, url]) => (
                    url && (
                      <a
                        key={platform}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-sm text-blue-600 hover:underline"
                        onClick={() => trackSocialClick(practice.name, platform, url)}
                      >
                        <span className="capitalize">{platform}</span>
                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    )
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PracticeShow;

